import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Share2, Martini, Sparkles, ArrowRight, Check } from "lucide-react";
import { ASSETS } from "../lib/content";
import { useCart } from "../lib/cart";

const STEPS = [
  { n: "01", t: "Air roasted fresh", d: "Your beans get roasted to order in Nashville, never left sitting stale in a warehouse." },
  { n: "02", t: "Packed & sealed", d: "Bagged at peak freshness, straight off the roast." },
  { n: "03", t: "On its way", d: "Shipped across North America. Tracking lands in your inbox." },
];

export default function ThanksPage({ onJoin }) {
  const { clear } = useCart();

  useEffect(() => { clear(); }, [clear]);

  const share = async () => {
    const url = window.location.origin;
    try {
      if (navigator.share) await navigator.share({ title: "Gremlin Coffee: air roasted, Nashville born", url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied. Spread the mischief."); }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't share. Try copying the link.");
    }
  };

  return (
    <main data-testid="thanks-page" className="pt-28 pb-24 px-5 md:px-10 min-h-screen">
      <div className="max-w-[900px] mx-auto text-center">
        <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <div className="relative inline-block">
            <img src={ASSETS.mascot} alt="" className="w-28 h-28 rounded-full object-cover ring-2 ring-ember mx-auto" />
            <span className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-ember text-void flex items-center justify-center">
              <Check size={18} strokeWidth={3} />
            </span>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember mt-8">Order received</p>
          <h1 className="font-display font-black uppercase tracking-tight text-cream text-4xl sm:text-5xl lg:text-6xl leading-[0.92] mt-4">
            You beautiful <span className="font-serif italic font-normal lowercase text-ember">gremlin.</span>
          </h1>
          <p className="font-mono text-sm text-cream/75 mt-5 max-w-md mx-auto leading-relaxed">
            Thanks for the order. The gremlins have already been warned. Smooth, air-roasted trouble is headed your way.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-14 text-left">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="border border-white/10 rounded-lg bg-ash p-6"
            >
              <p className="font-display font-black text-ember text-2xl">{s.n}</p>
              <h3 className="font-display font-extrabold uppercase tracking-tight text-cream text-lg mt-3">{s.t}</h3>
              <p className="font-mono text-xs text-cream/70 mt-2 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }} className="mt-14">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cream/45">While the beans roast</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <button
              onClick={share}
              data-testid="thanks-share-button"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest bg-ember text-void px-6 py-3.5 rounded-full font-medium hover:bg-emberDark transition-colors"
            >
              <Share2 size={14} /> Spread the mischief
            </button>
            <Link
              to="/recipes"
              data-testid="thanks-recipes-link"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-white/15 text-cream/70 px-6 py-3.5 rounded-full hover:border-ember hover:text-ember transition-colors"
            >
              <Martini size={14} /> Master the martini
            </Link>
            <Link
              to="/quiz"
              data-testid="thanks-quiz-link"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-white/15 text-cream/70 px-6 py-3.5 rounded-full hover:border-ember hover:text-ember transition-colors"
            >
              <Sparkles size={14} /> Find your next flavor
            </Link>
          </div>
          <button
            onClick={onJoin}
            data-testid="thanks-club-button"
            className="mt-8 font-mono text-[11px] uppercase tracking-widest text-ember hover:text-cream transition-colors inline-flex items-center gap-2"
          >
            Not in the Coffee Club yet? Fix that <ArrowRight size={13} />
          </button>
        </motion.div>
      </div>
    </main>
  );
}
